import React, { useContext, useEffect, useState } from "react";
import { StylePageSizes, StylePageStyle, StylePageSubscriptionSizes } from "../../../common/interfaces";
import {
  BuyDesignationInformation,
  RentalDesignationInformation,
  SubscriptionDesignationInformation,
} from "../../types";
import { useSession } from "./SessionProvider";
import { ServerGateway } from "../../services/ServerGateway";
import { SubscriptionService } from "../../services/SubscriptionService";
import { sortSizesFn } from "../../../common/util";

// Products that will always be sold as new
// This was pulled from magento logic for Google's structured data
const ALWAYS_NEW_CATEGORY_IDS = [
  15, // Fashion Solutions
  16, // Shapewear
  17, // Jewellery
  72, // Beauty
  151, // Hats
  164, // Face mask
];

export interface StyleProviderValue {
  retailPriceInCents: number;
  style: StylePageStyle;
  styleCode: string;

  buyInformation: BuyDesignationInformation | undefined;
  rentalInformation: RentalDesignationInformation | undefined;
  subscriptionInformation: SubscriptionDesignationInformation | undefined;
  isAlwaysNew: boolean;
}

export const StyleContextDefaults: StyleProviderValue = {
  retailPriceInCents: 50099,
  // @ts-ignore - intentional (too lazy to create placeholder). Will always be loaded from server
  style: {},
  styleCode: "",

  buyInformation: undefined,
  rentalInformation: undefined,
  subscriptionInformation: undefined,

  isAlwaysNew: false,
};

export const StyleContext = React.createContext<StyleProviderValue>(StyleContextDefaults);

export const StyleProvider: React.FC<{
  serverStyle: StylePageStyle;
  sizesResponse: StylePageSizes;
  subscriptionSizesResponse: StylePageSubscriptionSizes;
}> = ({ sizesResponse, serverStyle, subscriptionSizesResponse, children }) => {
  const { gcSession } = useSession();

  const [style, setStyle] = useState(serverStyle);
  const [sizes, setSizes] = useState(sizesResponse);
  const [subscriptionSizes, setSubscriptionSizes] = useState(subscriptionSizesResponse);

  const serverGateway = new ServerGateway();
  const subscriptionService = new SubscriptionService(serverGateway);
  const styleCode = style.style_code;

  let rentalInformation: StyleProviderValue["rentalInformation"] = undefined;
  let buyInformation: StyleProviderValue["buyInformation"] = undefined;
  let subscriptionInformation: StyleProviderValue["subscriptionInformation"] = undefined;

  if (style.designations_include_rental && style.rental_prices && style.rental_prices.length > 0) {
    const rentalPrices = style.rental_prices.map((prices) => ({
      rentalDays: prices.rental_days,
      priceInCents: prices.rental_price_in_cents,
      discountPriceInCents: prices.discount_rental_price_in_cents,
    }));

    rentalInformation = {
      prices: rentalPrices,
      sizes: sizes.rental.sort((a, b) => sortSizesFn(a.size, b.size)),
    };
  }

  if (style.designations_include_sale) {
    buyInformation = {
      prices: {
        buyPriceInCents: style.buy_price_in_cents!,
        discountBuyPriceInCents: style.discount_buy_price_in_cents!,
      },
      sizes: sizes.sale.sort((a, b) => sortSizesFn(a.size, b.size)),
    };
  }

  if (style.designations_include_subscription && subscriptionSizes) {
    const subSizes: SubscriptionDesignationInformation["sizes"] = [];
    Object.entries(subscriptionSizes).forEach((entry) => {
      const [productCode, availabilities] = entry;
      subSizes.push({
        productCode: productCode,
        // TODO: EXTRACT
        size: productCode.split("--")[1] || "onesize",
        available: availabilities.is_available_now,
      });
    });

    subscriptionInformation = {
      sizes: subSizes.sort((a, b) => sortSizesFn(a.size, b.size)),
    };
  }

  const isAlwaysNew = style.category_ids.some((r) => ALWAYS_NEW_CATEGORY_IDS.includes(r));

  const contextValue: StyleProviderValue = {
    retailPriceInCents: style.retail_price_in_cents,
    style,
    styleCode,

    buyInformation,
    rentalInformation,
    subscriptionInformation,

    isAlwaysNew,
  };

  const fetchSubscriptionAvailability = async () => {
    const { startDate, endDate } = await subscriptionService.getNextBillingPeriod();
    const tiers = await subscriptionService.getCustomersTiers(true);
    const data = await serverGateway.subscriptionAvailabilities(styleCode, tiers, startDate, endDate);

    if (data) {
      setSubscriptionSizes(data[styleCode]);
    }
  };

  const fetchStyleAndSizes = async () => {
    const stylePromise = serverGateway.queryStylePageStyle(styleCode);
    const sizesPromise = serverGateway.querySizes([styleCode]);

    const [sizesResponse, styleResponse] = await Promise.all([sizesPromise, stylePromise]);

    if (sizesResponse) {
      setSizes(sizesResponse[styleCode]);
    }
    if (styleResponse) {
      setStyle(styleResponse);
    }
  };

  useEffect(() => {
    fetchStyleAndSizes();
  }, []);

  useEffect(() => {
    if (gcSession && style.designations_include_subscription) {
      fetchSubscriptionAvailability();
    }
  }, [gcSession]);

  return <StyleContext.Provider value={contextValue}>{children}</StyleContext.Provider>;
};

export const useStyle = () => useContext(StyleContext);
